<template>
    <Head title="Users"/>

    <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
        Users
    </h1>

    <div class="bg-white py-6 px-4 mt-4 sm:p-6 shadow">
        <div class="flex justify-end">
            <search-filter :show-filter="false" v-model:value="form.search" class="w-1/4 max-w-md mr-4"
                           @reset="reset"></search-filter>
            <Button v-on:click="toggleUserSlideout(null)"
                    class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amaze-coral">
                <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Add New User
            </Button>
        </div>
        <div class="flex flex-col mt-3 space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                            <tr>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Code
                                </th>

                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Job Title
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Telephone
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Mobile
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Email
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Role
                                </th>
                                <th scope="col" class="relative px-6 py-3">
                                    <span class="sr-only">Edit</span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="users.data.length > 0" v-for="(user, index) in users.data" :key="index"
                                :class="{'bg-gray-100': index % 2}" class="hover:bg-gray-50 cursor-pointer">
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                    @click="toggleUserSlideout(user)">
                                    {{ user.name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ user.code }}
                                </td>

                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ user.job_title }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ user.telephone }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ user.mobile }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ user.email }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ titleCase(user.role) }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm">
                                    <div class="flex items-center justify-end gap-3">
                                        <button v-if="user.can_delete"
                                                type="button"
                                                @click.stop="deleteUser(user)"
                                                class="text-red-600 hover:text-red-800">
                                            <TrashIcon class="w-4 h-4"></TrashIcon>
                                        </button>
                                        <button type="button"
                                                @click.stop="toggleUserSlideout(user)"
                                                class="text-gray-700 hover:text-gray-900">
                                            <PencilAltIcon class="w-4 h-4"></PencilAltIcon>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            <tr v-else>
                                <td colspan="8" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">
                                    There are no users available for your search criteria.
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <Pagination :links="users.links"/>
        </div>
    </div>

    <CreateUser ref="createUserSlideout"/>

</template>


<script>

import Authenticated from "@/Layouts/Authenticated.vue";
import {Head} from "@inertiajs/vue3";
import Pagination from "@/Components/Pagination.vue";
import SearchFilter from "@/Components/SearchFilter.vue";
import debounce from 'lodash/debounce'
import pickBy from 'lodash/pickBy'
import mapValues from 'lodash/mapValues'
import CreateUser from "@/Components/SlideOuts/CreateUser";
import Button from "@/Components/Button.vue";
import {PencilAltIcon, TrashIcon} from "@heroicons/vue/solid";

export default {
    layout: Authenticated,
    components: {
        Button,
        CreateUser,
        Pagination,
        Head,
        SearchFilter,
        PencilAltIcon,
        TrashIcon
    },
    props: {
        users: Object,
        roles: Object
    },
    data() {
        return {
            form: {
                search: null
            }
        }
    },
    methods: {
        reset() {
            this.form = mapValues(this.form, () => null)
        },
        toggleUserSlideout(user = null) {
            this.$refs.createUserSlideout.updating = user !== null;
            this.$refs.createUserSlideout.form.id = user !== null ? user.id : null
            this.$refs.createUserSlideout.form.first_name = user !== null ? user.first_name : null
            this.$refs.createUserSlideout.form.last_name = user !== null ? user.last_name : null
            this.$refs.createUserSlideout.form.code = user !== null ? user.code : null
            this.$refs.createUserSlideout.form.job_title = user !== null ? user.job_title : null
            this.$refs.createUserSlideout.form.telephone = user !== null ? user.telephone : null
            this.$refs.createUserSlideout.form.mobile = user !== null ? user.mobile : null
            this.$refs.createUserSlideout.form.email = user !== null ? user.email : null
            this.$refs.createUserSlideout.form.role = user !== null ? user.role : null
            this.$refs.createUserSlideout.form.office_id = user !== null ? user.office_id : null
            this.$refs.createUserSlideout.form.additional_offices = user !== null ? user.additional_office_ids : null
            this.$refs.createUserSlideout.open();
        },
        deleteUser(user) {
            if (!window.confirm(`Delete ${user.name}? This will soft delete the user.`)) {
                return;
            }

            this.$inertia.delete(route('admin.users.user.delete', user.id));
        },
    },
    watch: {
        form: {
            deep: true,
            handler: debounce(function () {
                this.$inertia.get(route('admin.users.index'), pickBy(this.form), {preserveState: true})
            }, 500),
        },
    },
}
</script>
